import React from 'react'
import {
  List, ListItem, ListItemText, ListItemIcon, Button, Typography, Link,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ImageIcon from '@mui/icons-material/Image'
import TableChartIcon from '@mui/icons-material/TableChart'
import SlideshowIcon from '@mui/icons-material/Slideshow'
import ArticleIcon from '@mui/icons-material/Article'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import FolderZipIcon from '@mui/icons-material/FolderZip'
import CodeIcon from '@mui/icons-material/Code'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { formatFileSize, formatDate } from '../utils'

// Maps a file's extension or MIME type to an icon + accent color, so the
// resource list reads at a glance instead of being a wall of identical rows.
function iconFor({ name, mimeType }) {
  const ext = (name || '').split('.').pop().toLowerCase()
  const mt = (mimeType || '').toLowerCase()
  if (ext === 'pdf' || mt.includes('pdf')) {
    return { Icon: PictureAsPdfIcon, color: '#d32f2f' }
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic'].includes(ext)
      || mt.startsWith('image/')) {
    return { Icon: ImageIcon, color: '#43a047' }
  }
  if (['xls', 'xlsx', 'csv', 'ods', 'numbers'].includes(ext)
      || mt.includes('spreadsheet') || mt.includes('excel') || mt.includes('csv')) {
    return { Icon: TableChartIcon, color: '#2e7d32' }
  }
  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)
      || mt.includes('presentation') || mt.includes('powerpoint')) {
    return { Icon: SlideshowIcon, color: '#ef6c00' }
  }
  if (['doc', 'docx', 'odt', 'pages', 'rtf', 'txt', 'md'].includes(ext)
      || mt.includes('word') || mt.startsWith('text/')) {
    return { Icon: ArticleIcon, color: '#1565c0' }
  }
  if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'].includes(ext)
      || mt.startsWith('video/')) {
    return { Icon: VideoFileIcon, color: '#6a1b9a' }
  }
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac'].includes(ext)
      || mt.startsWith('audio/')) {
    return { Icon: AudioFileIcon, color: '#ad1457' }
  }
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext)
      || mt.includes('zip') || mt.includes('compressed')) {
    return { Icon: FolderZipIcon, color: '#6d4c41' }
  }
  if (['json', 'xml', 'yml', 'yaml', 'js', 'ts', 'rb', 'py', 'sh', 'html', 'css'].includes(ext)) {
    return { Icon: CodeIcon, color: '#37474f' }
  }
  return { Icon: InsertDriveFileIcon, color: '#5a5d63' }
}

// Renders a version's resources. Each one links straight to its slug-based
// direct-download URL — the browser downloads the file on click.
export default function ResourceList({ resources }) {
  if (!resources || resources.length === 0) {
    return <Typography color="text.secondary">У цій версії ще немає ресурсів.</Typography>
  }

  return (
    <List disablePadding>
      {resources.map((resource, index) => {
        const updatedAt = formatDate(resource.updated_at)
        const meta = [
          resource.documentation,
          formatFileSize(resource.file_size),
          updatedAt && `оновлено ${updatedAt}`,
        ]
          .filter(Boolean)
          .join(' · ')
        const { Icon, color } = iconFor({ name: resource.name, mimeType: resource.mime_type })
        return (
          <ListItem
            key={resource.id}
            divider={index < resources.length - 1}
            secondaryAction={
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                component="a"
                href={resource.download_path}
              >
                Завантажити
              </Button>
            }
          >
            <ListItemIcon sx={{ minWidth: 44, color }}>
              <Icon sx={{ fontSize: 32 }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Link
                  href={resource.download_path}
                  underline="hover"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    color: 'text.primary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {resource.name}
                </Link>
              }
              secondary={meta || null}
              sx={{ pr: 2 }}
            />
          </ListItem>
        )
      })}
    </List>
  )
}
